import type { CapacityLimit } from '@morpho-org/blue-sdk'

import { CapacityLimitReason } from '@morpho-org/blue-sdk'
import { isDefined } from '@morpho-org/morpho-ts'

/**
 * When IKR is available, raise Max to the full position so the user can reach the
 * insufficient-liquidity state (and its IKR entry), instead of capping at serviceable.
 */
export function resolveWithdrawInputMax({
  isInKindRedemptionEligible,
  userVaultAssets,
  maxWithdraw
}: {
  isInKindRedemptionEligible: boolean
  userVaultAssets: bigint | undefined
  maxWithdraw: CapacityLimit | undefined
}): CapacityLimit | undefined {
  if (
    !isInKindRedemptionEligible ||
    !isDefined(userVaultAssets) ||
    maxWithdraw?.limiter !== CapacityLimitReason.liquidity
  ) {
    return maxWithdraw
  }
  return { value: userVaultAssets, limiter: CapacityLimitReason.balance }
}

/** Vault cannot serve the whole position — shown on open, independent of input. */
export function isLiquidityLimitedState({
  userVaultAssets,
  maxWithdraw
}: {
  userVaultAssets: bigint | undefined
  maxWithdraw: CapacityLimit | undefined
}): boolean {
  return (
    isDefined(userVaultAssets) &&
    maxWithdraw?.limiter === CapacityLimitReason.liquidity &&
    userVaultAssets > maxWithdraw.value
  )
}

/** Requested amount exceeds serviceable liquidity but stays within the position. */
export function isInsufficientLiquidityState({
  inputAmount,
  userVaultAssets,
  maxWithdraw
}: {
  inputAmount: bigint
  userVaultAssets: bigint | undefined
  maxWithdraw: CapacityLimit | undefined
}): boolean {
  return (
    isDefined(userVaultAssets) &&
    maxWithdraw?.limiter === CapacityLimitReason.liquidity &&
    inputAmount > maxWithdraw.value &&
    inputAmount <= userVaultAssets
  )
}
